import { Subscription } from '../features/payments/enhanced-pricing-plans.type';

/**
 * Environment-aware subscription plan
 * Contains separate price IDs for development and production environments
 */
interface EnvironmentAwareSubscription extends Omit<Subscription, 'priceId'> {
  priceIds: {
    development: string;
    production: string;
  };
}

/**
 * Frontend Environment Pricing Service
 * Manages pricing plans based on the current environment (dev/prod)
 */
class EnvironmentPricingServiceClass {
  private currentEnvironment: 'development' | 'production';

  constructor() {
    // Determine environment from window location or environment variables
    this.currentEnvironment = this.determineEnvironment();
  }

  /**
   * Determine current environment
   */
  private determineEnvironment(): 'development' | 'production' {
    // Check if we're in development mode
    if (import.meta.env?.DEV ||
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('dev') ||
      window.location.hostname.includes('staging')) {
      return 'development';
    }

    // Default to production
    return 'production';
  }

  /**
   * Get environment-aware pricing plans
   */
  getPlansForCurrentEnvironment(): Subscription[] {
    const environmentAwarePlans: EnvironmentAwareSubscription[] = [
      {
        name: 'Free',
        price: '$0',
        priceIds: {
          development: 'price_1SOdUvPLwmdReis5krp8iGS7',
          production: 'price_1QxAbIAojyGeKZ9qWGN4PUa1'
        },
        features: [
          '1 interview session',
          '5 minutes max',
          'Basic AI feedback',
          '1 job analysis',
          '1 CV generation (one-time)',
          'No video storage'
        ],
        sessions: 1,
        frequency: 1,
        emailReminder: false,
        fileUpload: false,
        interviewLengths: ['5'],
        recordingLength: 5,
        jobAnalysisSessions: 1,
        cvGenerations: 1,
        cvStorageLimit: 0,
        exportFormats: ['pdf'],
        amount: 0,
        dataAccessPolicy: {
          maxVisibleSessions: 1,
          maxAccessibleCVs: 1,
          archivedSessionCount: 0,
          archivedCVCount: 0,
          lastPolicyUpdate: new Date()
        },
        skillsGapAnalysis: 'none',
        atsOptimization: false,
        companyResearch: false
      },
      {
        name: 'Basic',
        price: '$7',
        priceIds: {
          development: 'price_1SOdUjPLwmdReis5wa9sjAq5',
          production: 'price_1QxAcDAojyGeKZ9qli8qtc6H'
        },
        features: [
          '4 interview sessions/month',
          '15 minutes per session',
          'Full AI feedback',
          'Email reminders',
          '3 job analyses/month',
          '3 CVs per analysis',
          '7-day video storage',
          'Company research',
          'Basic skills gap analysis'
        ],
        sessions: 4,
        frequency: 4,
        emailReminder: true,
        fileUpload: false,
        interviewLengths: ['15'],
        recordingLength: 15,
        jobAnalysisSessions: 3,
        cvGenerations: 3,
        cvStorageLimit: 7,
        exportFormats: ['pdf', 'docx'],
        amount: 7,
        dataAccessPolicy: {
          maxVisibleSessions: 10,
          maxAccessibleCVs: 10,
          archivedSessionCount: 0,
          archivedCVCount: 0,
          lastPolicyUpdate: new Date()
        },
        skillsGapAnalysis: 'basic',
        atsOptimization: false,
        companyResearch: true
      },
      {
        name: 'Pro',
        price: '$15',
        priceIds: {
          development: 'price_1SOdUTPLwmdReis58tl8Rfu5',
          production: 'price_1QxAcoAojyGeKZ9qgHgE1BVZ'
        },
        features: [
          '10 interview sessions/month',
          '25 minutes per session',
          'Full AI feedback + analytics',
          'Email reminders',
          'File upload support',
          '8 job analyses/month',
          '3 CVs per analysis',
          '30-day video storage',
          'Company research',
          'Advanced skills gap analysis',
          'ATS optimization'
        ],
        sessions: 10,
        frequency: 10,
        emailReminder: true,
        fileUpload: true,
        interviewLengths: ['15', '25'],
        recordingLength: 25,
        jobAnalysisSessions: 8,
        cvGenerations: 3,
        cvStorageLimit: 30,
        exportFormats: ['pdf', 'docx'],
        amount: 15,
        dataAccessPolicy: {
          maxVisibleSessions: 30,
          maxAccessibleCVs: 30,
          archivedSessionCount: 0,
          archivedCVCount: 0,
          lastPolicyUpdate: new Date()
        },
        skillsGapAnalysis: 'advanced',
        atsOptimization: true,
        companyResearch: true
      },
      {
        name: 'Enterprise',
        price: '$30',
        priceIds: {
          development: 'price_1SOdUCPLwmdReis5sZA8ErAO',
          production: 'price_1QxAdpAojyGeKZ9qM1QSnlCV'
        },
        features: [
          '30 interview sessions/month',
          '30 minutes per session',
          'Full AI feedback + analytics',
          'Priority support',
          'File upload support',
          'Unlimited job analyses',
          'Unlimited CV generations',
          '90-day video storage',
          'Priority company research',
          'Advanced skills gap analysis',
          'Premium ATS optimization'
        ],
        sessions: 30,
        frequency: 30,
        emailReminder: true,
        fileUpload: true,
        interviewLengths: ['15', '30'],
        recordingLength: 30,
        jobAnalysisSessions: -1,
        cvGenerations: -1,
        cvStorageLimit: 90,
        exportFormats: ['pdf', 'docx'],
        amount: 30,
        dataAccessPolicy: {
          maxVisibleSessions: 90,
          maxAccessibleCVs: -1,
          archivedSessionCount: 0,
          archivedCVCount: 0,
          lastPolicyUpdate: new Date()
        },
        skillsGapAnalysis: 'advanced',
        atsOptimization: true,
        companyResearch: true
      }
    ];

    // Convert environment-aware plans to regular subscription plans
    return environmentAwarePlans.map(plan => ({
      ...plan,
      priceId: plan.priceIds[this.currentEnvironment]
    }));
  }

  /**
   * Get plan by price ID
   */
  getPlanByPriceId(priceId: string): Subscription | null {
    const plans = this.getPlansForCurrentEnvironment();
    return plans.find(plan => plan.priceId === priceId) || null;
  }

  /**
   * Get plan by name
   */
  getPlanByName(planName: string): Subscription | null {
    const plans = this.getPlansForCurrentEnvironment();
    return plans.find(plan => plan.name === planName) || null;
  }

  /**
   * Get current environment
   */
  getCurrentEnvironment(): 'development' | 'production' {
    return this.currentEnvironment;
  }

  /**
   * Validate price ID for current environment
   */
  validatePriceIdForEnvironment(priceId: string): boolean {
    const plans = this.getPlansForCurrentEnvironment();
    return plans.some(plan => plan.priceId === priceId);
  }

  /**
   * Convert subscription to current environment (frontend version)
   */
  async convertSubscriptionToCurrentEnvironment(subscription: any): Promise<Subscription | null> {
    // For frontend, we'll try to match by plan name
    const planByName = this.getPlanByName(subscription.name);
    if (planByName) {
      return planByName;
    }

    // Fallback to first plan (Free)
    const plans = this.getPlansForCurrentEnvironment();
    return plans[0] || null;
  }
}

// Export singleton instance
export const environmentPricingService = new EnvironmentPricingServiceClass();